import React, { useState, useEffect, useCallback } from "react";
import { rtdb } from "../firebase";
import { ref, get } from "firebase/database";
import "./AdminSummary.css";

// ⚡ Moved outside the component to guarantee a permanently stable reference address 
const NODES_CONFIG = ["Receive", "Treat", "Paint", "Pack", "Deliver"];

// 🌟 TargetFg and initialFilterContext props manage context parsing & explicit item auto-focus
export default function AdminSummary({ onBack, onSelectFgNumber, targetFg, initialFilterContext }) {
  const [selectedDate, setSelectedDate] = useState("");
  const [station, setStation] = useState("Receive");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [label]: prev[label] === false ? true : false,
    }));
  };

  // 🔹 Convert date format dd/mm/yyyy → yyyy-mm-dd safely
  const convertDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("/");
    if (parts.length !== 3) return "";
    const [d, m, y] = parts;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  };

  // 🔹 Standardize any time format to "HH:MM AM/PM" (Handles variations like PG/PTG)
  const formatToStandardTime = (timeStr) => {
    if (!timeStr || timeStr === "-") return "-";

    let clean = timeStr.toUpperCase().trim();
    clean = clean.replace(/\bPTG\b/i, "PM").replace(/\bPG\b/i, "AM");

    const digits = clean.replace(/[^0-9]/g, "");
    if (!digits) return timeStr;

    let h, m;
    if (digits.length <= 2) {
      h = parseInt(digits, 10);
      m = 0;
    } else {
      h = parseInt(digits.slice(0, digits.length - 2), 10);
      m = parseInt(digits.slice(-2), 10);
    }

    const isPM = clean.includes("PM") || (h >= 12 && h < 24);
    
    if (h > 12) h -= 12;
    if (h === 0) h = 12;

    const finalH = h.toString().padStart(2, "0");
    const finalM = m.toString().padStart(2, "0");
    const period = isPM ? "PM" : "AM";

    return `${finalH}:${finalM} ${period}`;
  };

  // 🔹 Helper for sorting
  const to24HourForSort = (standardTime) => {
    if (!standardTime || standardTime === "-") return "00:00";
    
    let [time, period] = standardTime.split(" ");
    let [h, m] = time.split(":").map(Number);

    if (period === "PM" && h < 12) h += 12;
    if (period === "AM" && h === 12) h = 0;

    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };

  // 🌟 Auto-resolve filter settings safely supporting multi-segment string payloads
  useEffect(() => {
    if (initialFilterContext && initialFilterContext.includes(" — ")) {
      const parts = initialFilterContext.split(" — ");
      
      let clickedStation = "";
      let clickedDate = "";

      if (parts.length === 3) {
        // Handled compound context pattern: "FGNumber — Station — Date"
        clickedStation = parts[1]?.trim();
        clickedDate = parts[2]?.trim();
      } else if (parts.length === 2) {
        // Standard chart context pattern: "Station — Date"
        clickedStation = parts[0]?.trim();
        clickedDate = parts[1]?.trim();
      }

      // ⚡ Safe referencing now utilizing our static array configurations
      if (NODES_CONFIG.includes(clickedStation)) {
        setStation(clickedStation);
      }
      
      if (clickedDate) {
        const formattedCalendarDate = convertDate(clickedDate.trim());
        setSelectedDate(formattedCalendarDate);
      }
    }
  }, [initialFilterContext]); // NODES_CONFIG is outside component scope, so it doesn't need to be tracked here

  // 🔹 Fetch Alternative with nested Batch processing (Returns list for layout sync chain)
  const fetchData = useCallback(async () => {
    if (!selectedDate) return [];
    setLoading(true);

    try {
      // Step 1: Fetch global profiles from Progress map
      const progressSnap = await get(ref(rtdb, "Progress"));
      const globalMetaMap = {};
      
      if (progressSnap.exists()) {
        progressSnap.forEach((child) => {
          const data = child.val();
          globalMetaMap[child.key] = {
            labelType: data.labelType || "-",
            quantity: Number(data.quantity || 0)
          };
        });
      }

      // Step 2: Query active station target
      const snap = await get(ref(rtdb, station));
      const list = [];

      if (snap.exists()) {
        snap.forEach((child) => {
          const data = child.val();
          let recordDate = "";
          let rawTime = "-";
          let batchLabelStr = "-"; 
          
          const currentFg = data.fgNumber || child.key; 

          if (station === "Receive") {
            recordDate = convertDate(data.receivingDate);
            rawTime = data.receivingTime;
          } else if (station === "Treat") {
            recordDate = convertDate(data.dateTreat);
            rawTime = data.timeTreat;
          } else if (station === "Paint") {
            recordDate = convertDate(data.datePaint);
            rawTime = data.timePaint;

            // Extract nested batchLabels specifically for Paint node entries
            if (data.batch && typeof data.batch === "object") {
              const labels = [];
              Object.values(data.batch).forEach((b) => {
                if (b && b.batchLabel) labels.push(b.batchLabel);
              });
              if (labels.length > 0) {
                batchLabelStr = labels.join(", "); 
              }
            }
          } else if (station === "Pack") {
            recordDate = convertDate(data.datePack);
            rawTime = data.timePack;
          } else if (station === "Deliver") {
            recordDate = convertDate(data.dateDeliver);
            rawTime = data.timeDeliver;
          }

          if (recordDate === selectedDate) {
            const meta = globalMetaMap[currentFg] || { labelType: "-", quantity: Number(data.quantity || 0) };
            list.push({
              fgNumber: currentFg,
              labelType: meta.labelType,
              batchLabel: batchLabelStr, 
              quantity: data.quantity ? Number(data.quantity) : meta.quantity,
              time: formatToStandardTime(rawTime),
            });
          }
        });
      }

      setRecords(list);
      return list;
    } catch (err) {
      console.error("Fetch operational tracking error:", err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [selectedDate, station]);

  // Synchronize state data on standard date / workflow dependency transitions
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 🌟 High-focus layout sync logic: Auto-expand matching accordions to surface highlighted targets
  useEffect(() => {
    if (targetFg && records.length > 0) {
      const targetMatch = records.find((r) => r.fgNumber === targetFg);
      if (targetMatch && targetMatch.labelType) {
        setExpandedGroups((prev) => ({
          ...prev,
          [targetMatch.labelType]: true, // Guarantee group partition visibility
        }));
      }
    }
  }, [targetFg, records]);

  const totalQty = records.reduce((sum, r) => sum + r.quantity, 0);

  const groupedRecords = {};
  records.forEach((r) => {
    if (!groupedRecords[r.labelType]) groupedRecords[r.labelType] = [];
    groupedRecords[r.labelType].push(r);
  });

  return (
    <div className="admin-summary-page">
      <div className="top-bar">
        <h2 className="header-summary">Daily Summary</h2>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label>Target Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Station Workflow</label>
          <select value={station} onChange={(e) => setStation(e.target.value)}>
            {NODES_CONFIG.map((node) => (
              <option key={node}>{node}</option>
            ))}
          </select>
        </div>
        
        <button className="refresh-btnn" onClick={fetchData} disabled={loading}>
          {loading ? "Syncing..." : "Refresh"}
        </button>
      </div>

      <div className="summary-wrapper">
        <div className="summary-card main-stat">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <h3>{station} Throughput</h3>
            {loading ? (
              <div className="loader"></div>
            ) : (
              <div className="stat-grid">
                <div className="stat-item">
                  <span className="stat-text">Total Pieces</span>
                  <span className="stat-total">{totalQty}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-text">Total Labels</span>
                  <span className="stat-total">{records.length}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {!loading && Object.keys(groupedRecords).length > 0 && (
          <div className="label-summary">
            <h3>Breakdown by Type</h3>
            <div className="breakdown-list">
              {Object.entries(groupedRecords).map(([label, recs]) => {
                const sumQty = recs.reduce((sum, r) => sum + r.quantity, 0);
                const labelCount = recs.length;

                return (
                  <div key={label} className="breakdown-pill">
                    <span className="type-name">{label}</span>
                    <span className="type-qty">
                      {" "} | <strong>{labelCount}</strong> {labelCount === 1 ? "label" : "labels"}
                      {" "} = <strong>{sumQty}</strong> <small>pcs</small>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {!loading && (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>FG Number</th>
                <th>Label Type</th>
                {station === "Paint" && <th>Batch Label</th>}
                <th>Quantity</th>
                <th>Completion Time</th>
              </tr>
            </thead>
            <tbody>
            {Object.keys(groupedRecords).length > 0 ? (
              Object.entries(groupedRecords).map(([label, recs]) => {
                const isExpanded = expandedGroups[label] !== false;

                return (
                  <React.Fragment key={label}>
                    <tr 
                      className="label-header togglable-header" 
                      onClick={() => toggleGroup(label)}
                      style={{ cursor: "pointer", userSelect: "none" }}
                      title={isExpanded ? "Click to collapse" : "Click to expand"}
                    >
                      <td colSpan={station === "Paint" ? "5" : "4"}>
                        <span style={{ 
                          display: "inline-block", 
                          marginRight: "8px", 
                          transition: "transform 0.2s ease",
                          transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)"
                        }}>
                          ▼
                        </span>
                        Category: {label} ({recs.length} items)
                      </td>
                    </tr>

                    {isExpanded && recs
                      .sort((a, b) =>
                        to24HourForSort(a.time).localeCompare(to24HourForSort(b.time))
                      )
                      .map((r, i) => (
                        <tr 
                          key={r.fgNumber || i} 
                          className={`data-row clickable-row ${targetFg === r.fgNumber ? "highlighted-row-intent" : ""}`} 
                          onClick={() => onSelectFgNumber && onSelectFgNumber(r.fgNumber)}
                          title="Click to view Progress Details"
                        >
                          <td className="fg-cell">{r.fgNumber}</td>
                          <td><span className="badge">{r.labelType}</span></td>
                          {station === "Paint" && (
                            <td className="batch-cell">
                              <span className="batch-pill">{r.batchLabel}</span>
                            </td>
                          )}
                          <td className="qty-cell">{r.quantity}</td>
                          <td className="time-cell">{r.time}</td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={station === "Paint" ? "5" : "4"} className="no-data">
                  <div className="empty-state">
                    <p>No records found for the selected criteria.</p>
                  </div>
                </td>
              </tr>
            )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}